import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    TextField,
    Chip,
    InputAdornment,
    Stack,
    IconButton,
    Button,
    Collapse,
    Paper,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { TopicCategory } from './ContestacaoTopics';

interface TopicSelectorProps {
    topics: TopicCategory[];
    selectedTopics: string[];
    onChange: (selected: string[]) => void;
    title?: string;
    description?: string;
}

const TopicSelector: React.FC<TopicSelectorProps> = ({
    topics,
    selectedTopics,
    onChange,
    title = "Tópicos a Suscitar",
    description = "Selecione as matérias de defesa, preliminares e mérito que devem ser abordadas na contestação."
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState<string | false>(false);

    // Filtrar tópicos baseado na busca
    const filteredTopics = useMemo(() => {
        if (!searchTerm) return topics;

        return topics.map(category => {
            const matchesCategory = category.name.toLowerCase().includes(searchTerm.toLowerCase());
            const filteredItems = category.items.filter(item =>
                item.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (matchesCategory || filteredItems.length > 0) {
                return {
                    ...category,
                    items: matchesCategory ? category.items : filteredItems
                };
            }
            return null;
        }).filter(Boolean) as TopicCategory[];
    }, [topics, searchTerm]);

    // Expandir automaticamente se houver busca
    React.useEffect(() => {
        if (searchTerm) {
            setExpanded(filteredTopics.length > 0 ? filteredTopics[0].id : false);
        }
    }, [searchTerm, filteredTopics]);

    const handleToggleTopic = (topic: string) => {
        const newSelected = selectedTopics.includes(topic)
            ? selectedTopics.filter(t => t !== topic)
            : [...selectedTopics, topic];
        onChange(newSelected);
    };

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const clearSelection = () => {
        onChange([]);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {description}
                </Typography>
            </Box>

            {/* Barra de Busca e Chips Selecionados */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Pesquisar tópicos (ex: Prescrição, Inépcia...)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{ mb: 2 }}
                />

                {/* Chips de Seleção */}
                {selectedTopics.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="primary" fontWeight="bold">
                                {selectedTopics.length} tópico(s) selecionado(s)
                            </Typography>
                            <Button size="small" onClick={clearSelection} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                                Limpar todos
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectedTopics.map(topic => (
                                <Chip
                                    key={topic}
                                    label={topic}
                                    onDelete={() => handleToggleTopic(topic)}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    deleteIcon={<ClearIcon />}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Lista de Categorias */}
            <Box>
                {filteredTopics.length > 0 ? (
                    filteredTopics.map((category) => (
                        <Accordion
                            key={category.id}
                            expanded={expanded === category.id || !!searchTerm} // Mantém expandido se tiver busca
                            onChange={handleAccordionChange(category.id)}
                            disableGutters
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:not(:last-child)': { borderBottom: 0 },
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                        {category.name}
                                    </Typography>
                                    {/* Badge count for selected items in this category */}
                                    {category.items.some(item => selectedTopics.includes(item)) && (
                                        <Chip
                                            label={category.items.filter(item => selectedTopics.includes(item)).length}
                                            size="small"
                                            color="primary"
                                            sx={{ height: 20, minWidth: 20, px: 0.5, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Stack>
                                    {category.items.map((item) => (
                                        <FormControlLabel
                                            key={item}
                                            control={
                                                <Checkbox
                                                    checked={selectedTopics.includes(item)}
                                                    onChange={() => handleToggleTopic(item)}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">{item}</Typography>}
                                            sx={{ ml: 0 }}
                                        />
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Nenhum tópico encontrado para "{searchTerm}"
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TopicSelector;
